// ==============================
// Payroll Setup Payload Builders
// ==============================

import dayjs from "dayjs";

// --- Helper Functions ---
const getItemTypeId = (valueStr) => {
    const itemTypeOptions = [
        { label: "Wages", value: "wages", id: 8 },
        { label: "Additions", value: "additions", id: 9 },
        { label: "Deductions", value: "deductions", id: 10 },
        { label: "Employee Taxes", value: "employee_taxes", id: 11 },
        { label: "Employer Taxes", value: "employer_taxes", id: 12 },
    ];
    const found = itemTypeOptions.find(opt => opt.value === valueStr);
    return found ? found.id : 0;
};

const filterOptionMap = {
    begins_with: 0,
    contains: 1,
    not_contains: 2,
    ends_with: 3,
    equals: 4,
    not_equals: 5
};

const createFilter = (filter) => ({
    CriterionID: filter?.id || "",
    SearchTerm: filter?.term || "",
    FilterOption: filter?.term ? (filterOptionMap[filter.option] ?? 1) : 4
});

// ==============================
// Filtered Payroll Load Payload
// ==============================
export const buildFilteredPayrollLoadPayload = (filters, selectedCorp, page) => {
    let corpFilter;

    if (selectedCorp && selectedCorp.ID && selectedCorp.ID !== "000000000000000000000000000000000000") {
        corpFilter = {
            CriterionID: selectedCorp.ID,
            SearchTerm: "",
            FilterOption: 0   // equals
        };
    } else {
        corpFilter = createFilter(filters.corporation);
    }

    return {
        CorpFilter: corpFilter,
        ItemTypeFilter: createFilter(filters.itemType),
        ItemNameFilter: createFilter(filters.itemName),
        DeptFilter: createFilter(filters.department),
        JobTitleFilter: createFilter(filters.jobtitle),
        AccountFilteroption: createFilter(filters.account),
        LiabilityAccFilteroption: createFilter(filters.liabilityAccount),
        StatusFilteroption: createFilter(filters.status),
        // ItemStatusFilter: statusValue,
        PageNum: page,
    };
};

// ==============================
// Save New Payroll Setup Payload
// ==============================
export const buildSavePayrollSetupPayload = (values, selectedCloneCorps, wageTypes, payTypes) => {
    const sourceCorpId = values.corporation?.corpValue;
    const cloneCorpIds = Array.isArray(selectedCloneCorps)
        ? selectedCloneCorps.filter(id => String(id) !== String(sourceCorpId))
        : [];

    const payload = {
        ID: 0, // 0 for new items
        CorporationID: values.corporation?.corpValue,
        ItemType: getItemTypeId(values.itemType),
        ItemName: values.itemName,
        IsSave: true, // Always true for creating a new item
        IsNew: true,  // Always true for this form
        IsImport: false,
        IsCloning: values.clone,
        IsUpdate: false,
        IsApply: false,
        IsActive: 1, // New items are always active

        ...(values.clone && cloneCorpIds.length > 0 && {
            CorpIDs: cloneCorpIds
        }),

        PayrollDeptJobDetails: values.entries.map(entry => ({
            Id: 0, // 0 for new entries
            IncDeptID: entry.department,
            JobID: entry.jobTitle,
            AccountID: entry.expenseCoa,
            ...(values.itemType === "employer_taxes" && { LiabilityAccountID: entry.payableCoa || null }),
             AccountName: entry.expenseCoaName,
            ...(values.itemType === "employer_taxes" && { LiabilityAccountID: entry.payableCoa || null, LiabilityAccountName: entry.payableCoaName || null }),
            IsActive: 1 // New detail entries are active by default
        }))
    };

    // Add wage type and pay type for wages
    if (values.itemType === "wages") {
        // Find selected wage type details
        const wageDetails = wageTypes.find(w => w.ID === values.wageType);

        payload.WageTypeID = wageDetails?.ID || null;
        // payload.WageSourceID = wageDetails?.SourceID || null;
        // payload.WageSourceType = wageDetails?.SourceType || null;

        if (values.payType) {
            const payDetails = payTypes.find(p => p.ID === values.payType);
            payload.PayTypeID = payDetails?.ID || null;
            // payload.PaySourceID = payDetails?.SourceID || null;
            // payload.PaySourceType = payDetails?.SourceType || null;
        }
    }

    return payload;
};

// ==============================
// Update Payroll Setup Payload
// ==============================
export const buildUpdatePayrollSetupPayload = (values, editData, selectedCloneCorps, originalEntries) => {
    const sourceCorpId = values.corporation?.corpValue;
    const cloneCorpIds = Array.isArray(selectedCloneCorps)
        ? selectedCloneCorps.filter(id => String(id) !== sourceCorpId)
        : [];


    return {
        PayrollItemID: String(editData.payrollItemId),
        CorporationID: sourceCorpId,
        ItemType: getItemTypeId(values.itemType),
        ItemName: values.itemName,
        IsActive: values.active ? 1 : 0,
        IsSave: true,
        // IsNew: !values.clone,          // ✅ VERY IMPORTANT
        IsApply: false,
        IsUpdate: true,
        IsNew: false,
        IsImport: false,
        IsCloning: values.clone,
        IsCoaupdate: values.updateCOA || false,
        CoaupdateEffectiveDate: (values.updateCOA && values.updateCOADate) ? dayjs(values.updateCOADate).format("YYYY-MM-DD") : null,

        // ✅ ONLY include wage fields when itemType === "wages"
        ...(values.itemType === "wages" && values.wageType && {
            WageTypeID: values.wageType
        }),
        ...(values.itemType === "wages" && values.payType && {
            PayTypeID: values.payType
        }),
        // ...(values.itemType === "wages" && values.wageSourceID && {
        //     WageSourceID: values.wageSourceID,
        //     WageSourceType: values.wageSourceType
        // }),
        // ...(values.itemType === "wages" && values.paySourceID && {
        //     PaySourceID: values.paySourceID,
        //     PaySourceType: values.paySourceType
        // }),

        ...(values.clone && cloneCorpIds.length > 0 && {
            CorpIDs: cloneCorpIds
        }),

        PayrollDeptJobDetails: values.entries.map(currentEntry => {
            let isDeptJobUpdated = false;
            // Find the original state of this row using its id. New rows won't have an originalEntry.
            const originalEntry = originalEntries.find(e => e.id === currentEntry.id);

            if (originalEntry) {
                const deptChanged = String(originalEntry.department || "") !== String(currentEntry.department || "");
                const jobChanged = String(originalEntry.jobTitle || "") !== String(currentEntry.jobTitle || "");
                const expenseCoaChanged = String(originalEntry.expenseCoa || "") !== String(currentEntry.expenseCoa || "");
                const payableCoaChanged = String(originalEntry.payableCoa || "") !== String(currentEntry.payableCoa || "");
                const statusChanged = Boolean(originalEntry.isActive) !== Boolean(currentEntry.isActive);

                if (deptChanged || jobChanged || expenseCoaChanged || payableCoaChanged || statusChanged) {
                    isDeptJobUpdated = true;
                }
            } else {
                isDeptJobUpdated = true;
            }

            return {
                Id: currentEntry.id || 0,
                PayrollItemID: String(editData.payrollItemId),
                IncDeptID: currentEntry.department,
                JobID: currentEntry.jobTitle,
                AccountID: currentEntry.expenseCoa,
                AccountName: currentEntry.expenseCoaName,
                ...(values.itemType === "employer_taxes" && { LiabilityAccountID: currentEntry.payableCoa || null, LiabilityAccountName: currentEntry.payableCoaName || null }),
                IsActive: currentEntry.isActive ? 1 : 0,
                IsDeptJobUpdated: isDeptJobUpdated,
            };
        })
    };
};

// ==============================
// Import Payroll Setup Payload
// ==============================
export const buildImportPayrollSetupPayload = (rows, selectedCorp) => {
    // Group by ItemName and Corporation
    const grouped = {};

    rows.forEach(row => {
        const key = `${row.ItemName}_${row.Corporation}`;

        if (!grouped[key]) {
            grouped[key] = {
                CorporationID: row.Corporation,
                ItemType: row.ItemType,
                ItemName: row.ItemName,
                WageTypeID: row.WageTypeID || null,
                PayTypeID: row.PayTypeID || null,
                IsActive: 1,
                DeptJobDetails: []
            };
        }

        const deptJob = {
            IncDeptID: row.Department,
            JobID: row.JobTitle,
            AccountID: row.ExpenseCOA,
            LiabilityAccountID: row.PayableCOA || null,
            IsActive: 1
        };

        grouped[key].DeptJobDetails.push(deptJob);
    });

    return {
        CorpId: selectedCorp.corpValue,
        ImportPayrollItem: Object.values(grouped),
    };
};